<script setup lang="ts">
import type { SearchCounts, SearchResult } from "~/types/contentSearch"
import * as v from "valibot"
import { VideoSchema } from "~/models/Content/schemas"
import { NuxtLink } from "#components"

const props = defineProps<{
  results: SearchResult[]
  totalCounts: SearchCounts
  filteredCounts: number
  loading: boolean
}>()

const emit = defineEmits<{
  (e: "loadMore"): void
}>()

const productStore = useProductVariantsStore()
const loadMoreObserverListEl = ref<HTMLDivElement | null>(null)
let loadMoreObserver: IntersectionObserver | null = null

// Cleanup the observer when the component is unmounted
onUnmounted(() => {
  if (loadMoreObserver && loadMoreObserverListEl.value) {
    loadMoreObserver.unobserve(loadMoreObserverListEl.value)
    loadMoreObserver.disconnect()
  }
})

onMounted(() => {
  loadMoreObserver = new IntersectionObserver(entries => {
    if (
      props.results.length === 0 ||
      props.results.length >= props.totalCounts.total ||
      props.loading ||
      !entries.some(entry => entry.isIntersecting)
    )
      return

    emit("loadMore")
  })

  if (loadMoreObserverListEl.value) {
    loadMoreObserver.observe(loadMoreObserverListEl.value)
  }
})

// Re-observe when results change
watch(
  () => props.results.length,
  () => {
    if (loadMoreObserver && loadMoreObserverListEl.value) {
      loadMoreObserver.unobserve(loadMoreObserverListEl.value)
      loadMoreObserver.observe(loadMoreObserverListEl.value)
    }
  },
)
</script>

<template>
  <div class="overflow-auto scrollbar-hide no-scrollbar">
    <h3 class="sr-only">Søkeresultat</h3>
    <div
      class="mb-3 md:mb-5 italic font-national2 text-base font-bold md:text-xl/[24px]"
    >
      {{ filteredCounts }} treff
    </div>
    <ul class="space-y-5 w-full">
      <li
        v-for="result in results"
        :key="result.article.id"
        class="rounded-2xl"
        :class="
          result.article.template !== 'Video' ? 'bg-platform-paper text-platform-black' : 'bg-[--theme-darker] [&>*]:text-white'
        "
      >
        <component
          :is="result.hasAccess ? NuxtLink : 'div'"
          :to="result.link"
          class="relative flex flex-col md:flex-row justify-between w-full"
          :aria-label="[
            !result.hasAccess ? 'Låst' : '',
            result.article.title,
            result.article.template === 'Video' ? 'Video' : '',
            `Kapittel: ${result.subject.priority}`,
            result.subject.title,
          ].filter(Boolean).join('. ')"
        >
          <USearchResultItem
            :template="result.article.template === 'Video' ? 'video' : 'article'"
            :result="result"
            :is-free="!productStore.activeVariant?.userHasLicence && result.hasAccess"
          >
            <template v-if="result.article.template === 'Video'" #badge>
              <UContentBadge content="Video" class="text-white" />
            </template>
            <template v-if="result.article.template === 'Video'" #secondaryBadge>
              <UBadge variant="filled" passive>
                <span class="sr-only">Varighet:</span>
                {{ getMinutesAndSeconds(result.article.relatedItems.find(r => v.is(VideoSchema, r))?.metadata.duration) }}
              </UBadge>
            </template>
          </USearchResultItem>
        </component>
      </li>
    </ul>
    <div
      ref="loadMoreObserverListEl"
      class="w-full h-10"
      aria-hidden="true"
    />

    <div
      v-if="loading && results.length > 0"
      class="flex justify-center items-center mt-8"
    >
      <u-icon name="spinner" class="animate-spin mr-2" />
      <span class="text-white font-national2"
      >Laster flere resultater...</span
      >
    </div>
  </div>
</template>
